import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { difference } from "underscore";
import { useDispatch, useSelector } from "react-redux";
import { Select, Checkbox } from "antd";
import {
    AppDispatch,
    IRevstackChannels,
    IRevstackChannelState,
    SubModule,
    TChannelSelectOptions,
} from "@ts/common-types";
import {
    filterSelectOptionsWithIdAndName,
    titleCase,
    getClientId,
} from "@ts/utils";
import { updateRevstackChannel } from "@features/channelSlice";
import { updateRevstackChannels } from "@ts/retrieve-data";
import { getActiveModule } from "@/ts/utils";
import { resetRevstackDashboardWidgetData } from "@features/revstackWidgetsSlice";
import { REVSTACK_MULTI_CHANNEL_MODULES } from "@/ts/constants";
import ChannelImage from "../misc/ChannelImage";
import { applyCurrentFiltersWithHash } from "@/ts/revstack/filters";

const { Option } = Select;

const NON_EDITABLE_CONTAINER_STYLES: React.CSSProperties = {
    minWidth: "90px",
    height: "35px",
    padding: "8px 12px",
    backgroundColor: "#F0F4F8",
};

const NON_EDITABLE_TEXT_STYLES: React.CSSProperties = {
    fontSize: 14,
    color: "#2D2D2D",
    fontWeight: 500,
};

const findChannelById = (
    channels: IRevstackChannels[],
    id: number
): IRevstackChannels | undefined => {
    return channels.find((channel) => channel.id === id);
};

const findChannelsByIds = (
    channels: IRevstackChannels[],
    ids: number[]
): IRevstackChannels[] => {
    return channels.filter((channel) => ids.includes(channel.id));
};

const isAllChannelsSelected = (
    allChannelIds: number[],
    selectedChannelIds: number[]
): boolean => {
    return (
        allChannelIds.length > 0 &&
        allChannelIds.every((id) => selectedChannelIds.includes(id))
    );
};

const getAllChannelIds = (channels: IRevstackChannels[]): number[] => {
    return channels.map((channel) => channel.id);
};

const getSelectedChannelsSafe = (
    selectedChannels: number[] | number | undefined
): number[] => {
    if (Array.isArray(selectedChannels)) return selectedChannels;
    return selectedChannels ? [selectedChannels] : [];
};

const getDisplayText = (
    displayValue: number[],
    channels: IRevstackChannels[],
    isMultiChannelModule: boolean
): string => {
    if (!isMultiChannelModule) {
        const selectedChannel = findChannelsByIds(channels, displayValue)?.[0];
        return selectedChannel?.name
            ? titleCase(selectedChannel.name)
            : "Select Channel";
    }

    if (displayValue.length === 0) {
        return "Select Channels";
    }

    const allChannelIds = getAllChannelIds(channels);
    const isAllSelected = isAllChannelsSelected(allChannelIds, displayValue);

    if (isAllSelected) {
        return "All Channels";
    }

    const selectedChannels = findChannelsByIds(channels, displayValue);

    if (selectedChannels.length === 0) {
        return "Select Channels";
    }

    if (selectedChannels.length === 1) {
        return selectedChannels[0]?.name
            ? titleCase(selectedChannels[0].name)
            : "Unknown Channel";
    }

    const firstChannel = selectedChannels[0];
    if (!firstChannel?.name) {
        return `${selectedChannels.length} channels selected`;
    }

    const firstChannelName = titleCase(firstChannel.name);
    const displayLabel =
        firstChannelName.length > 15
            ? firstChannelName.substring(0, 15) + "..."
            : firstChannelName;
    return displayLabel;
};

interface RevstackChannelsSelectProps {
    editable?: boolean;
}

interface NonEditableChannelDisplayProps {
    displayValue: number[];
    revstackChannels: IRevstackChannels[];
    isMultiChannelModule: boolean;
}

interface EditableChannelSelectProps {
    revstackChannels: IRevstackChannels[];
    selectedChannels: number[];
    revstackChannel: number[];
    isMultiChannelModule: boolean;
    onChange: (value: number | number[]) => void;
    onOpenChange: (open: boolean) => void;
    tagRender: (tagProps: any) => React.ReactElement | null;
    disableChannelSelection: boolean;
}

const NonEditableChannelDisplay: React.FC<NonEditableChannelDisplayProps> = ({
    displayValue,
    revstackChannels,
    isMultiChannelModule,
}) => {
    const displayText = getDisplayText(
        displayValue,
        revstackChannels,
        isMultiChannelModule
    );
    const safeDisplayValue = getSelectedChannelsSafe(displayValue);
    const allChannelIds = getAllChannelIds(revstackChannels);
    const isAllSelected =
        isMultiChannelModule && Array.isArray(displayValue)
            ? isAllChannelsSelected(allChannelIds, displayValue)
            : false;

    const firstChannel =
        safeDisplayValue.length > 0
            ? findChannelById(revstackChannels, safeDisplayValue[0])
            : undefined;

    return (
        <div
            className="d-flex align-items-center gap-1 rounded-2"
            style={NON_EDITABLE_CONTAINER_STYLES}
        >
            {firstChannel?.logo_url && !isAllSelected && (
                <ChannelImage channel={firstChannel} size="small" />
            )}
            <div
                className="flex-fill text-start"
                style={NON_EDITABLE_TEXT_STYLES}
            >
                {displayText}
            </div>
        </div>
    );
};

const EditableChannelSelect: React.FC<EditableChannelSelectProps> = ({
    revstackChannels,
    selectedChannels,
    revstackChannel,
    isMultiChannelModule,
    onChange,
    onOpenChange,
    tagRender,
    disableChannelSelection,
}) => {
    return (
        <div className="position-relative" style={{ width: "150px" }}>
            <Select
                showSearch={!isMultiChannelModule}
                id="revstack-channel-selection"
                className="channel-selector w-100"
                value={
                    isMultiChannelModule ? selectedChannels : revstackChannel[0]
                }
                onChange={onChange}
                onOpenChange={onOpenChange}
                mode={isMultiChannelModule ? "multiple" : undefined}
                size="middle"
                disabled={disableChannelSelection}
                filterOption={
                    !isMultiChannelModule
                        ? (input, option: TChannelSelectOptions) => {
                              const childrenProps =
                                  option.children?.props?.children[1]?.props
                                      .children;
                              return childrenProps?.length
                                  ? filterSelectOptionsWithIdAndName(
                                        input,
                                        childrenProps
                                    )
                                  : false;
                          }
                        : undefined
                }
                tagRender={isMultiChannelModule ? tagRender : undefined}
                dropdownClassName={
                    isMultiChannelModule
                        ? "revstack-multi-select-checkbox"
                        : undefined
                }
                popupRender={
                    isMultiChannelModule
                        ? (menu) => (
                              <MultiChannelDropdown
                                  revstackChannels={revstackChannels}
                                  selectedChannels={selectedChannels}
                                  onChange={onChange}
                              />
                          )
                        : undefined
                }
            >
                {!isMultiChannelModule &&
                    revstackChannels.map((data: IRevstackChannels) => (
                        <Option key={data.id} value={data.id}>
                            <span>
                                <ChannelImage channel={data} size="large" />
                                <span className="channel-name">
                                    {titleCase(data.name)}
                                    <div className="d-none">{`${data.id}##`}</div>
                                </span>
                            </span>
                        </Option>
                    ))}
            </Select>
        </div>
    );
};

const MultiChannelDropdown = ({
    revstackChannels,
    selectedChannels,
    onChange,
}: {
    revstackChannels: IRevstackChannels[];
    selectedChannels: number[];
    onChange: (value: number[]) => void;
}) => {
    const allChannelIds = getAllChannelIds(revstackChannels);
    const isAllSelected = isAllChannelsSelected(
        allChannelIds,
        selectedChannels
    );
    const handleAllChannelsToggle = () =>
        onChange(isAllSelected ? [] : allChannelIds);
    const handleChannelToggle = (channelId: number) => {
        const currentValues = getSelectedChannelsSafe(selectedChannels);
        const isSelected = currentValues.includes(channelId);
        let newValues;
        if (isSelected) {
            newValues = currentValues.filter((v) => v !== channelId);
        } else {
            newValues = [...currentValues, channelId];
        }
        onChange(newValues);
    };

    return (
        <div className="revstack-multi-select-checkbox">
            <div
                className="dropdown-item"
                role="button"
                onClick={handleAllChannelsToggle}
            >
                <Checkbox checked={isAllSelected} />
                <div className="item-label d-flex align-items-center gap-2">
                    All Channels
                </div>
            </div>

            {revstackChannels.map((channel: IRevstackChannels) => {
                const isChecked = getSelectedChannelsSafe(
                    selectedChannels
                ).includes(channel.id);
                return (
                    <div
                        key={channel.id}
                        className="dropdown-item"
                        role="button"
                        onClick={() => handleChannelToggle(channel.id)}
                    >
                        <Checkbox checked={isChecked} />
                        <div className="item-label d-flex align-items-center gap-2">
                            <ChannelImage channel={channel} size="medium" />
                            {titleCase(channel.name)}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

const RevstackChannelsSelect: React.FC<RevstackChannelsSelectProps> = ({
    editable = true,
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const revstackChannels = useSelector(
        (state: any) => state.revstackChannelsList.value
    );
    const revstackChannelsListUpdateId = useSelector(
        (state: any) => state.revstackChannelsList.updateId
    );
    const revstackChannel = useSelector(
        (state: IRevstackChannelState) => state.revstackChannel.value
    );
    const [selectedChannels, setSelectedChannels] = useState<number[]>([]);
    const activeModule = getActiveModule().activeModule;
    const activeClientId: number = getClientId();
    const prevActiveModule = useRef(activeModule);
    const prevActiveClientId = useRef(activeClientId);
    const prevChannelsListUpdateId = useRef(revstackChannelsListUpdateId);
    const hasInitialChannelBeenSet = useRef(false);
    const isExpectingChannelUpdate = useRef(false);
    const isMultiChannelModule = useMemo(() => {
        //TODO: Is there any way we can leverage this from the config?
        return REVSTACK_MULTI_CHANNEL_MODULES.includes(
            activeModule as SubModule
        );
    }, [activeModule]);

    const relativeFilters = useSelector((state: any) => state.relativeFilters.value);

    const disableChannelSelection = useMemo(() => {
        return !editable || !revstackChannels.length || (activeModule === SubModule.PRODUCT_ANALYSIS && relativeFilters?.skus?.length > 0);
    }, [editable, revstackChannels, activeModule, relativeFilters]);


    // Track module changes - this will be true when module actually changes
    const hasModuleChanged = prevActiveModule.current !== activeModule;

    // Update the previous module reference after checking for changes
    useEffect(() => {
        if (hasModuleChanged) {
            isExpectingChannelUpdate.current = true;
        }
        prevActiveModule.current = activeModule;
    }, [activeModule, hasModuleChanged]);

    const allChannelIds = useMemo(
        () => getAllChannelIds(revstackChannels),
        [revstackChannels]
    );
    const safeSelectedChannels = useMemo(
        () => getSelectedChannelsSafe(selectedChannels),
        [selectedChannels]
    );
    const isAllSelected = useMemo(
        () => isAllChannelsSelected(allChannelIds, safeSelectedChannels),
        [allChannelIds, safeSelectedChannels]
    );

    const previousValueOnOpen = useRef<number | number[]>();

    const onChange = async (value: number | number[]) => {
        dispatch(resetRevstackDashboardWidgetData());

        if (isMultiChannelModule) {
            setSelectedChannels(value as number[]);
        } else {
            dispatch(updateRevstackChannel([value as number]));
        }

        await applyCurrentFiltersWithHash({
            channel: isMultiChannelModule ? (value as number[]) : [value as number],
        });
    };

    const onOpenChange = (open: boolean) => {
        if (!isMultiChannelModule) return;

        if (open) {
            // Store the current selectedChannels state when opening
            previousValueOnOpen.current = [...selectedChannels];
        } else {
            const isSelectionUnchanged =
                JSON.stringify(selectedChannels) ===
                JSON.stringify(previousValueOnOpen.current);

            // Also check if revstackChannel is different from selectedChannels
            const isRevstackChannelDifferent =
                JSON.stringify(revstackChannel) !==
                JSON.stringify(selectedChannels);
            if (!isSelectionUnchanged || isRevstackChannelDifferent) {
                const channelsToUpdate =
                    selectedChannels.length > 0
                        ? selectedChannels
                        : revstackChannel;

                // For multi-channel module, only update if we have at least 2 channels
                // if (isMultiChannelModule && channelsToUpdate.length < 2) {
                //     return;
                // }

                dispatch(resetRevstackDashboardWidgetData());
                //TODO: This has to be made into number[] while data integrationg with @nijo
                dispatch(updateRevstackChannel(channelsToUpdate));
            }
        }
    };

    /**
     * Fetch channels when client changes
     * Sets a flag when client changes to gate the next channel selection update
     * Only fetches when editable=true (not for display-only components like drilldown drawer)
     */
    useEffect(() => {
        // Skip all channel fetching logic if component is not editable
        if (!editable) {
            return;
        }

        const isClientChange = prevActiveClientId.current !== 0 && 
                               prevActiveClientId.current !== activeClientId;
        
        if (isClientChange) {
            isExpectingChannelUpdate.current = true;
        }
        
        // Only fetch channels if there's an actual client change, initial load, or no channels available
        const shouldFetchChannels = isClientChange || 
                                   prevActiveClientId.current === 0 || 
                                   revstackChannels.length === 0;

        if (shouldFetchChannels) {
            dispatch(updateRevstackChannels());
            setSelectedChannels([]);
        }
        
        // Update ref AFTER checking to properly detect the next change
        prevActiveClientId.current = activeClientId;
    }, [activeClientId, editable, revstackChannels.length]);

    /**
     * Handle channel initialization and updates
     * 
     * Triggers channel selection update on:
     * 1. Initial load
     * 2. Module change
     * 3. Channels list loaded after client change (gated by isExpectingChannelUpdate flag)
     * 
     * Uses channelsListUpdateId timestamp to detect when new channels are loaded.
     * The isExpectingChannelUpdate flag prevents spurious updates during app initialization.
     * Only runs when editable=true (not for display-only components like drilldown drawer)
     */
    useEffect(() => {
        // Skip all channel initialization logic if component is not editable
        if (!editable) {
            return;
        }

        if (!revstackChannels.length || !allChannelIds.length) return;

        const isInitialLoad = !hasInitialChannelBeenSet.current;

        // Check if channels list updateId has changed (indicates channels were just loaded)
        // Only trigger on updateId change if we're expecting a channel update (after client/module change)
        const channelsListJustLoaded = 
            !isInitialLoad &&
            isExpectingChannelUpdate.current &&
            prevChannelsListUpdateId.current !== 0 &&
            prevChannelsListUpdateId.current !== revstackChannelsListUpdateId;

        if (isInitialLoad || hasModuleChanged || channelsListJustLoaded) {
            if (isMultiChannelModule && editable) {
                // Multi-channel module: ensure multiple channels
                if (isInitialLoad) {
                    dispatch(updateRevstackChannel(allChannelIds));
                    setSelectedChannels(allChannelIds);
                    hasInitialChannelBeenSet.current = true;
                } else if (channelsListJustLoaded) {
                    // Client changed - check if current channels exist in new client's channels
                    const validCurrentChannels = revstackChannel.filter(id => allChannelIds.includes(id));
                    const channelsToSet = validCurrentChannels.length > 0 ? validCurrentChannels : allChannelIds;
                    
                    dispatch(updateRevstackChannel(channelsToSet));
                    setSelectedChannels(channelsToSet);
                    applyCurrentFiltersWithHash({ channel: channelsToSet });
                } else if (
                    hasModuleChanged &&
                    (!revstackChannel?.length || revstackChannel.length === 1)
                ) {
                    const channelsToUse =
                        selectedChannels.length > 1
                            ? selectedChannels
                            : allChannelIds;
                    dispatch(updateRevstackChannel(channelsToUse));
                    if (selectedChannels.length === 1) {
                        setSelectedChannels(allChannelIds);
                    }
                }
            } else {
                // Single-channel module: ensure only one channel
                if (isInitialLoad) {
                    if (allChannelIds[0]) {
                        dispatch(updateRevstackChannel([allChannelIds[0]]));
                        hasInitialChannelBeenSet.current = true;
                    }
                } else if (channelsListJustLoaded) {
                    // Client changed - check if current channel exists in new client's channels
                    const currentChannelId = revstackChannel[0];
                    const channelToSet = (currentChannelId && allChannelIds.includes(currentChannelId)) 
                        ? currentChannelId 
                        : allChannelIds[0];
                    
                    if (channelToSet) {
                        dispatch(updateRevstackChannel([channelToSet]));
                        applyCurrentFiltersWithHash({ channel: [channelToSet] });
                    }
                }
            }
            // Reset the flag after any channel update dispatch
            if (channelsListJustLoaded || hasModuleChanged) {
                isExpectingChannelUpdate.current = false;
            }
        }
        // Update the previous updateId
        prevChannelsListUpdateId.current = revstackChannelsListUpdateId;
    }, [
        hasModuleChanged,
        isMultiChannelModule,
        revstackChannels.length,
        allChannelIds.length,
        editable,
        dispatch,
        allChannelIds,
        selectedChannels,
        revstackChannelsListUpdateId,
    ]);

    const handleMultiChannelMode = useCallback(() => {

        // If both selectedChannels and revstackChannel are empty and we have a hash channel, skip
        if (selectedChannels.length === 0 && !revstackChannel?.length) {
            return;
        }

        if (selectedChannels.length === 0) {
            // For multi-channel mode, check if revstackChannel has multiple valid channels
            if (
                revstackChannel?.length > 1 &&
                revstackChannel.every((id) => allChannelIds.includes(id))
            ) {
                // If already has multiple channels, use those
                setSelectedChannels(revstackChannel);
            } else {
                // Default to all channels for better UX in multi-select mode
                setSelectedChannels(allChannelIds);
            }
            return;
        }

        const validSelectedChannels = selectedChannels.filter((id) =>
            allChannelIds.includes(id)
        );

        if (validSelectedChannels.length !== selectedChannels.length) {
            // Instead of resetting to all, keep only the valid ones
            setSelectedChannels(validSelectedChannels);
        }
    }, [selectedChannels, allChannelIds, revstackChannel]);

    const handleSingleChannelMode = useCallback(() => {
        const firstChannel = allChannelIds[0];

        // Reset if: channel is empty (but not if we have a hash channel) OR channel is invalid
        const needsReset =
            (!revstackChannel?.length) ||
            (revstackChannel?.length > 0 && !allChannelIds.includes(revstackChannel[0]));

        if (needsReset) {
            dispatch(updateRevstackChannel([firstChannel]));
        }
    }, [allChannelIds, revstackChannel, dispatch]);

    // Sync selectedChannels with revstackChannel when Redux state changes (e.g., from hash filters)
    useEffect(() => {
        if (!editable || !revstackChannels.length || !revstackChannel?.length) return;

        if (isMultiChannelModule) {
            // For multi-channel modules, sync selectedChannels with revstackChannel
            const validChannels = revstackChannel.filter(id => allChannelIds.includes(id));
            
            if (validChannels.length > 0 &&
                (validChannels.length !== selectedChannels.length ||
                 difference(validChannels, selectedChannels).length > 0)) {
                setSelectedChannels(validChannels);
            }
        }
    }, [revstackChannel, allChannelIds, isMultiChannelModule, editable, revstackChannels.length]);

    // Handle mode-specific logic when channels or module changes
    useEffect(() => {
        // Skip all mode-specific logic if component is not editable
        if (!editable) {
            return;
        }

        if (!revstackChannels.length) return;

        if (isMultiChannelModule && editable) {
            handleMultiChannelMode();
        } else {
            handleSingleChannelMode();
        }
    }, [
        isMultiChannelModule,
        revstackChannels.length,
        handleMultiChannelMode,
        handleSingleChannelMode,
        editable,
    ]);

    const tagRender = (tagProps: any) => {
        const { value: tagValue } = tagProps;

        if (isAllSelected) {
            if (tagValue === safeSelectedChannels[0]) {
                return (
                    <span
                        className="revstack-multi-select-tag-item"
                        key="all-channels"
                    >
                        All Channels
                    </span>
                );
            }
            return null;
        }

        const tagIndex = safeSelectedChannels.indexOf(tagValue);
        if (tagIndex === 0) {
            const channel = findChannelById(revstackChannels, tagValue);
            const label = channel?.name
                ? titleCase(channel.name)
                : String(tagValue);
            const displayLabel =
                label.length > 15 ? label.substring(0, 15) + "..." : label;
            return (
                <span className="revstack-multi-select-tag" key={tagValue}>
                    {displayLabel}
                </span>
            );
        } else if (tagIndex === 1) {
            const remainingCount = safeSelectedChannels.length - 1;
            return (
                <span className="revstack-multi-select-tag" key="count">
                    +{remainingCount}
                </span>
            );
        }

        return null;
    };

    if (!editable) {
        const displayValue = isMultiChannelModule
            ? selectedChannels
            : revstackChannel;
        return (
            <NonEditableChannelDisplay
                displayValue={displayValue}
                revstackChannels={revstackChannels}
                isMultiChannelModule={isMultiChannelModule}
            />
        );
    }

    return (
        <EditableChannelSelect
            revstackChannels={revstackChannels}
            selectedChannels={safeSelectedChannels}
            revstackChannel={revstackChannel}
            isMultiChannelModule={isMultiChannelModule}
            onChange={onChange}
            onOpenChange={onOpenChange}
            tagRender={tagRender}
            disableChannelSelection={disableChannelSelection}
        />
    );
};

export default RevstackChannelsSelect;
